import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Send, RotateCcw, Maximize2, Trash2, Loader2, ChevronUp, ChevronDown, Download, FileSpreadsheet } from "lucide-react";
import { exportToCSV, exportToExcel, exportRebarToCSV, exportRebarToExcel } from "./utils/exportUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// External APS authentication backend (configurable via environment)
const APS_AUTH_BACKEND = process.env.REACT_APP_APS_AUTH_BACKEND || "https://revit-viewer-backend.onrender.com";

// Default URN for demo model
const DEFAULT_URN = "dXJuOmFkc2sub2JqZWN0czpvcy5vYmplY3Q6YW50b25yZXZpdGJ1Y2tldDIwMjYvYnJ6MDAxLmR3Zng";

// Helper: Normalize category from element name as fallback
const normalizeFromName = (name) => {
  if (!name) return "";
  const n = name.toLowerCase();
  
  // Check for common BIM categories in name
  if (n.includes("door")) return "Doors";
  if (n.includes("window")) return "Windows";
  if (n.includes("wall")) return "Walls";
  if (n.includes("floor")) return "Floors";
  if (n.includes("roof")) return "Roofs";
  if (n.includes("ceiling")) return "Ceilings";
  if (n.includes("column")) return "Columns";
  if (n.includes("beam")) return "Beams";
  if (n.includes("stair")) return "Stairs";
  if (n.includes("railing")) return "Railings";
  if (n.includes("pipe")) return "Pipes";
  if (n.includes("duct")) return "Ducts";
  if (n.includes("fixture")) return "Fixtures";
  if (n.includes("equipment")) return "Equipment";
  if (n.includes("furniture") || n.includes("chair") || n.includes("table") || n.includes("desk")) return "Furniture";
  if (n.includes("gutter")) return "Gutters";
  if (n.includes("generic")) return "Generic Models";
  
  return ""; // Let caller decide default
};

function App() {
  const [viewer, setViewer] = useState(null);
  const [modelDataCache, setModelDataCache] = useState([]);
  const [isBuilding, setIsBuilding] = useState(false);
  const [viewerStatus, setViewerStatus] = useState({ text: "Connecting", color: "bg-slate-400" });
  const [objectCount, setObjectCount] = useState(0);
  const [selectionCount, setSelectionCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  const viewerRef = useRef(null);
  const chatRef = useRef(null);
  const viewerInstanceRef = useRef(null);

  // Handle resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (viewerInstanceRef.current && viewerInstanceRef.current.impl) {
        viewerInstanceRef.current.resize();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll chat to bottom when messages change
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  // Fetch APS token from external Render backend
  const getAccessToken = useCallback(async (onTokenReady) => {
    try {
      const response = await axios.get(`${APS_AUTH_BACKEND}/api/auth/token`);
      onTokenReady(response.data.access_token, response.data.expires_in);
    } catch (err) {
      console.error("Auth error:", err);
      setViewerStatus({ text: "Auth Failed", color: "bg-red-500" });
      addMessage("system", "Authentication failed. Please check APS credentials.");
    }
  }, []);

  // Initialize Autodesk Viewer
  const initViewer = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (viewerInstanceRef.current) {
        viewerInstanceRef.current.finish();
        viewerInstanceRef.current = null;
      }

      const container = viewerRef.current;
      if (!container || container.clientWidth === 0) {
        setTimeout(() => initViewer().then(resolve).catch(reject), 300);
        return;
      }

      const options = { env: 'AutodeskProduction', getAccessToken };
      
      window.Autodesk.Viewing.Initializer(options, () => {
        const v = new window.Autodesk.Viewing.GuiViewer3D(container);
        v.start();
        v.setTheme("light-theme");
        
        v.addEventListener(window.Autodesk.Viewing.SELECTION_CHANGED_EVENT, () => {
          setSelectionCount((v.getSelection() || []).length);
        });
        
        viewerInstanceRef.current = v;
        setViewer(v);
        resolve(v);
      });
    });
  }, [getAccessToken]);

  // Build model index for AI search
  const buildIndex = useCallback(async (v) => {
    if (!v || !v.model || isBuilding) return;
    
    setIsBuilding(true);
    setViewerStatus({ text: "Indexing", color: "bg-amber-500" });

    const tree = v.model.getInstanceTree();
    if (!tree) {
      setIsBuilding(false);
      setViewerStatus({ text: "Ready", color: "bg-emerald-500" });
      return;
    }

    // Helper: Get category by walking up the tree to find category node
    const getCategoryFromTree = (dbId) => {
      let currentId = dbId;
      const visited = new Set();
      
      // Known Revit category patterns (including localized)
      const knownCategories = [
        "Doors", "Windows", "Walls", "Floors", "Roofs", "Ceilings",
        "Columns", "Structural Columns", "Beams", "Structural Framing",
        "Structural Rebar", "Rebar", "Structural Foundations",
        "Stairs", "Railings", "Furniture", "Furniture Systems",
        "Pipes", "Pipe Fittings", "Pipe Accessories",
        "Ducts", "Duct Fittings", "Duct Accessories",
        "Plumbing Fixtures", "Mechanical Equipment", "Electrical Fixtures",
        "Generic Models", "Fascias", "Gutters", "Pads", "Casework",
        "Curtain Panels", "Curtain Wall Mullions", "Site",
        // Russian equivalents (from the model)
        "Двери", "Окна", "Стены", "Полы", "Крыши", "Потолки",
        "Колонны", "Лестницы", "Мебель", "Трубы", "Воздуховоды"
      ];
      
      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const parentId = tree.getNodeParentId(currentId);
        
        if (parentId) {
          const parentName = tree.getNodeName(parentId);
          
          if (parentName) {
            // Clean name - remove count suffix like " (7)"
            const cleanName = parentName.replace(/\s*\(\d+\)\s*$/, "").trim();
            
            // Check for exact match
            if (knownCategories.includes(cleanName)) {
              return cleanName;
            }
            
            // Check for case-insensitive match
            const nameLower = cleanName.toLowerCase();
            for (const cat of knownCategories) {
              if (nameLower === cat.toLowerCase()) {
                return cat;
              }
            }
            
            // Also check if the name contains a known category (for compound names)
            for (const cat of knownCategories) {
              if (cleanName.includes(cat) || cat.includes(cleanName)) {
                return cat;
              }
            }
          }
        }
        currentId = parentId;
      }
      return "";
    };

    // Get all leaf node IDs
    const allIds = [];
    const crawl = (id) => {
      allIds.push(id);
      tree.enumNodeChildren(id, (childId) => crawl(childId));
    };
    crawl(tree.getRootId());

    // Filter for leaf nodes (actual geometry)
    const leafIds = allIds.filter(id => {
      let hasFrag = false;
      tree.enumNodeFragments(id, () => { hasFrag = true; }, false);
      return hasFrag;
    });

    const chunkSize = 400;
    const results = [];
    const stats = {};

    for (let i = 0; i < leafIds.length; i += chunkSize) {
      const chunk = leafIds.slice(i, i + chunkSize);
      const props = await new Promise(res => v.model.getBulkProperties(chunk, null, res));

      for (const item of props) {
        const p = item.properties || [];
        
        // PRIORITY 1: Get category from tree hierarchy (most reliable for Revit)
        let cat = getCategoryFromTree(item.dbId);
        
        // PRIORITY 2: Look for "Category" property
        if (!cat) {
          const catProp = p.find(x => 
            x.displayName.toLowerCase() === "category" ||
            x.displayName === "Category"
          );
          if (catProp?.displayValue) {
            cat = String(catProp.displayValue)
              .replace(/^OST_/i, "")
              .replace(/^Revit /i, "")
              .trim();
          }
        }
        
        // PRIORITY 3: Infer from element name
        if (!cat && item.name) {
          cat = normalizeFromName(item.name);
        }
        
        // Default fallback
        if (!cat) cat = "Other";
        
        const level = p.find(x => 
          x.displayName.toLowerCase().includes("level") ||
          x.displayName.toLowerCase() === "story" ||
          x.displayName.toLowerCase() === "reference level"
        )?.displayValue || "";
        
        const material = p.find(x => 
          x.displayName.toLowerCase().includes("material") ||
          x.displayName.toLowerCase() === "structural material"
        )?.displayValue || "";
        
        const area = parseFloat(p.find(x => x.displayName.toLowerCase().includes("area"))?.displayValue) || 0;
        const volume = parseFloat(p.find(x => x.displayName.toLowerCase().includes("volume"))?.displayValue) || 0;

        stats[cat] = (stats[cat] || 0) + 1;
        
        // Create searchBlob combining all searchable text for fuzzy matching
        const allVals = p.map(x => String(x.displayValue || "")).join(" ");
        const searchBlob = `${cat} ${item.name || ""} ${level} ${material} ${allVals}`.toLowerCase();

        results.push({
          dbId: item.dbId,
          name: item.name || "",
          category: cat,
          level: level,
          material: material,
          area: area,
          volume: volume,
          searchBlob: searchBlob,
          properties: p.reduce((acc, prop) => {
            acc[prop.displayName] = prop.displayValue;
            return acc;
          }, {})
        });
      }
      setObjectCount(results.length);
    }

    setModelDataCache(results);
    setIsBuilding(false);
    setViewerStatus({ text: "Ready", color: "bg-emerald-500" });

    // Index complete - no message needed, UI shows the status card
    console.log(`Index complete: ${results.length} elements, categories: ${Object.keys(stats).length}`);
  }, [isBuilding]);

  // Load model into viewer
  const loadModel = useCallback(async (rawUrn) => {
    try {
      let v = viewerInstanceRef.current;
      if (!v) {
        v = await initViewer();
      }
      
      setViewerStatus({ text: "Loading", color: "bg-amber-500" });
      setShowOverlay(true);

      const documentId = 'urn:' + rawUrn.trim().replace(/^urn:/, "");

      window.Autodesk.Viewing.Document.load(
        documentId,
        (doc) => {
          const viewable = doc.getRoot().getDefaultGeometry();
          v.loadDocumentNode(doc, viewable).then(() => {
            v.addEventListener(
              window.Autodesk.Viewing.OBJECT_TREE_CREATED_EVENT,
              () => {
                setViewerStatus({ text: "Ready", color: "bg-emerald-500" });
                setShowOverlay(false);
                buildIndex(v);
              },
              { once: true }
            );
          });
        },
        (err) => {
          setViewerStatus({ text: "Failed", color: "bg-red-500" });
          setShowOverlay(false);
          addMessage("system", "Failed to load model. Check URN or authentication.");
          console.error("Model load error:", err);
        }
      );
    } catch (e) {
      setViewerStatus({ text: "Error", color: "bg-red-500" });
      console.error("Model loading error:", e);
    }
  }, [initViewer, buildIndex]);

  // Initialize on mount - runs only once
  useEffect(() => {
    // Check if Autodesk Viewer script is loaded
    const checkAndInit = () => {
      if (window.Autodesk && window.Autodesk.Viewing) {
        initViewer().then(() => loadModel(DEFAULT_URN));
      } else {
        setTimeout(checkAndInit, 500);
      }
    };
    
    checkAndInit();

    return () => {
      if (viewerInstanceRef.current) {
        viewerInstanceRef.current.finish();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add message to chat (with optional takeoff/rebar data for export)
  const addMessage = (role, content, takeoff = null, rebarWeight = null) => {
    setMessages(prev => [...prev, { role, content, takeoff, rebarWeight }]);
  };

  // Send query to AI
  const askAI = async () => {
    if (!inputText.trim() || isLoading) return;

    const query = inputText.trim();
    setInputText("");
    addMessage("user", query);
    setIsLoading(true);

    // Auto-expand sheet on mobile when sending message
    if (isMobile && !sheetExpanded) {
      setSheetExpanded(true);
    }

    try {
      const response = await axios.post(`${API}/ai`, {
        text: query,
        modelData: modelDataCache
      });

      const { answer, dbIds, responseType, takeoff, rebarWeight } = response.data;
      
      // Add message with appropriate export data
      if (responseType === "rebar_weight" && rebarWeight) {
        addMessage("ai", answer, null, rebarWeight);
      } else if (responseType === "takeoff" && takeoff) {
        addMessage("ai", answer, takeoff, null);
      } else {
        addMessage("ai", answer);
      }

      // Isolate elements in viewer if we have dbIds
      if (dbIds && dbIds.length > 0 && viewerInstanceRef.current) {
        viewerInstanceRef.current.isolate(dbIds);
        viewerInstanceRef.current.fitToView(dbIds);
      }
    } catch (err) {
      console.error("AI query error:", err);
      addMessage("system", "Failed to process query. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Viewer controls
  const handleFitView = () => {
    if (viewerInstanceRef.current) {
      viewerInstanceRef.current.fitToView();
    }
  };

  const handleReset = () => {
    if (viewerInstanceRef.current) {
      viewerInstanceRef.current.isolate([]);
      viewerInstanceRef.current.fitToView();
      viewerInstanceRef.current.clearSelection();
    }
  };

  const handleClearChat = () => {
    setMessages([{ role: "system", content: "Chat cleared. BIM engine index is still active." }]);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      askAI();
    }
  };

  // Quick action - directly send query
  const handleQuickAction = async (query) => {
    if (isLoading || modelDataCache.length === 0) return;
    
    addMessage("user", query);
    setIsLoading(true);

    // Auto-expand sheet on mobile when sending message
    if (isMobile && !sheetExpanded) {
      setSheetExpanded(true);
    }

    try {
      const response = await axios.post(`${API}/ai`, {
        text: query,
        modelData: modelDataCache
      });

      const { answer, dbIds, responseType, takeoff, rebarWeight } = response.data;
      
      // Add message with appropriate export data
      if (responseType === "rebar_weight" && rebarWeight) {
        addMessage("ai", answer, null, rebarWeight);
      } else if (responseType === "takeoff" && takeoff) {
        addMessage("ai", answer, takeoff, null);
      } else {
        addMessage("ai", answer);
      }

      // Isolate elements in viewer if we have dbIds
      if (dbIds && dbIds.length > 0 && viewerInstanceRef.current) {
        viewerInstanceRef.current.isolate(dbIds);
        viewerInstanceRef.current.fitToView(dbIds);
      }
    } catch (err) {
      console.error("AI query error:", err);
      addMessage("system", "Failed to process query. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Quick action buttons - include rebar weight
  const quickActions = [
    { label: "Summary", query: "model summary" },
    { label: "Pipe Length", query: "total pipe length" },
    { label: "Rebar Weight", query: "rebar weight" },
    { label: "Show Doors", query: "show all doors" }
  ];

  // Determine status class
  const getStatusClass = () => {
    if (viewerStatus.text === "Ready") return "ready";
    if (viewerStatus.text === "Loading" || viewerStatus.text === "Indexing") return "loading";
    if (viewerStatus.text === "Failed" || viewerStatus.text === "Auth Failed" || viewerStatus.text === "Error") return "error";
    return "";
  };

  // AI Capabilities list
  const capabilities = [
    "Show any model category",
    "Count elements by type or level",
    "Calculate total length, area, volume",
    "Compare quantities across levels",
    "Generate quantity takeoffs",
    "Highlight results in 3D viewer"
  ];

  // Panel content - reusable for both desktop and mobile
  const renderPanelContent = () => (
    <>
      <div className="chat-area" ref={chatRef} data-testid="chat-messages">
        {messages.length <= 2 && (
          <>
            <div className="capability-card">
              <div className="capability-title">This AI can:</div>
              <div className="capability-list">
                {capabilities.map((cap, i) => (
                  <div key={i} className="capability-item">{cap}</div>
                ))}
              </div>
            </div>
            {modelDataCache.length > 0 && (
              <div className="index-card">
                <div className="index-status">
                  <span className="index-dot"></span>
                  <span className="index-title">Auto-Indexing Complete</span>
                </div>
                <div className="index-count">{objectCount.toLocaleString()} elements indexed</div>
              </div>
            )}
          </>
        )}
        {messages.filter(m => m.role !== "system" || messages.length > 2).map((msg, idx) => (
          <div 
            key={idx} 
            className={`message ${msg.role === "user" ? "message-user" : msg.role === "ai" ? "message-ai" : "message-system"}`}
            data-testid={`message-${idx}`}
          >
            <div className="message-content" dangerouslySetInnerHTML={{ 
              __html: msg.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') 
            }} />
            {/* Material Takeoff Report Card with Export Buttons */}
            {msg.takeoff && msg.takeoff.total !== null && (
              <div className="takeoff-card" data-testid={`takeoff-card-${idx}`}>
                <div className="takeoff-header">Material Takeoff</div>
                <div className="takeoff-export-buttons">
                  <button 
                    className="export-btn"
                    onClick={() => exportToCSV(msg.takeoff, 'BIM Model')}
                    data-testid={`export-csv-${idx}`}
                  >
                    <Download size={14} />
                    CSV
                  </button>
                  <button 
                    className="export-btn"
                    onClick={() => exportToExcel(msg.takeoff, 'BIM Model')}
                    data-testid={`export-excel-${idx}`}
                  >
                    <FileSpreadsheet size={14} />
                    Excel
                  </button>
                </div>
              </div>
            )}
            {/* Structural Takeoff Report Card for Rebar Weight */}
            {msg.rebarWeight && msg.rebarWeight.totalWeightKg > 0 && (
              <div className="takeoff-card structural" data-testid={`rebar-card-${idx}`}>
                <div className="takeoff-header">Structural Takeoff</div>
                <div className="takeoff-export-buttons">
                  <button 
                    className="export-btn"
                    onClick={() => exportRebarToCSV(msg.rebarWeight, 'BIM Model')}
                    data-testid={`export-rebar-csv-${idx}`}
                  >
                    <Download size={14} />
                    CSV
                  </button>
                  <button 
                    className="export-btn"
                    onClick={() => exportRebarToExcel(msg.rebarWeight, 'BIM Model')}
                    data-testid={`export-rebar-excel-${idx}`}
                  >
                    <FileSpreadsheet size={14} />
                    Excel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="message message-ai message-loading">
            <Loader2 className="spin-loader-small" size={14} />
            <span>Analyzing model...</span>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="bim-app" data-testid="bim-app">
      {/* ==================== VIEWER SECTION ==================== */}
      <div className="viewer-section" data-testid="viewer-panel">
        {/* Floating Profile Block - visible on both mobile and desktop */}
        <div className="profile-block" data-testid="profile-block">
          <div className="avatar-badge">AZ</div>
          <div className="profile-info">
            <span className="profile-name">Anton Zolotarov</span>
            <span className="profile-tagline">BIM AI Platform</span>
          </div>
        </div>

        {/* Status Pill - Floating */}
        <div className={`status-pill ${getStatusClass()}`} data-testid="status-pill">
          <span className={`status-dot ${getStatusClass()}`} data-testid="status-dot"></span>
          <span data-testid="viewer-status">{viewerStatus.text.toUpperCase()}</span>
        </div>

        {/* Viewer Canvas */}
        <div className="viewer-container">
          {showOverlay && (
            <div className="viewer-overlay" data-testid="viewer-overlay">
              <Loader2 className="spin-loader" size={28} />
              <p>Loading model...</p>
            </div>
          )}
          <div ref={viewerRef} className="viewer-canvas" data-testid="viewer-canvas"></div>
        </div>

        {/* Floating Viewer Controls - Mobile Only */}
        {isMobile && (
          <div className="viewer-controls" data-testid="viewer-controls">
            <button className="viewer-control-btn" onClick={handleFitView} data-testid="fit-view-btn">
              <Maximize2 size={20} />
            </button>
            <button className="viewer-control-btn" onClick={handleReset} data-testid="reset-view-btn">
              <RotateCcw size={20} />
            </button>
          </div>
        )}
      </div>

      {/* ==================== MOBILE: BOTTOM SHEET ==================== */}
      {isMobile && (
        <div 
          className={`bottom-sheet ${sheetExpanded ? 'expanded' : 'collapsed'}`} 
          data-testid="bottom-sheet"
        >
          {/* Drag Handle */}
          <div 
            className="sheet-handle" 
            onClick={() => setSheetExpanded(!sheetExpanded)}
            data-testid="sheet-handle"
          >
            <div className="handle-bar"></div>
          </div>

          {/* Sheet Header */}
          <div className="sheet-header">
            <div className="sheet-title-row">
              <span className="sheet-title">BIM Intelligence Engine</span>
              <button 
                className="expand-btn"
                onClick={() => setSheetExpanded(!sheetExpanded)}
                data-testid="expand-btn"
              >
                {sheetExpanded ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
              </button>
            </div>
            <button className="clear-btn" onClick={handleClearChat} data-testid="clear-chat-btn">
              <Trash2 size={13} />
              Clear
            </button>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions" data-testid="quick-actions">
            {quickActions.map((action, idx) => (
              <button 
                key={idx}
                className="quick-btn"
                onClick={() => handleQuickAction(action.query)}
                disabled={isLoading || modelDataCache.length === 0}
                data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
              >
                {action.label}
              </button>
            ))}
          </div>

          {/* Sheet Content - Scrollable Chat */}
          <div className="sheet-content">
            {renderPanelContent()}
          </div>

          {/* Input Area */}
          <div className="input-area">
            <div className="input-wrapper">
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about quantities, levels, or categories..."
                className="chat-input"
                rows={2}
                disabled={isLoading}
                data-testid="chat-input"
              />
              <button 
                className="send-btn"
                onClick={askAI}
                disabled={isLoading || !inputText.trim() || modelDataCache.length === 0}
                data-testid="send-btn"
              >
                <Send size={16} />
              </button>
            </div>
            <div className="input-hint">
              Example: "Total pipe length" or "Show rebar on Level 1"
            </div>
          </div>
        </div>
      )}

      {/* ==================== DESKTOP: SIDE PANEL ==================== */}
      {!isMobile && (
        <div className="intelligence-panel" data-testid="chat-panel">
          <div className="panel-header">
            <span className="panel-title">BIM Intelligence Engine</span>
            <button className="clear-btn" onClick={handleClearChat} data-testid="clear-chat-btn-desktop">
              <Trash2 size={13} />
              Clear
            </button>
          </div>

          {renderPanelContent()}

          <div className="quick-actions" data-testid="quick-actions-desktop">
            {quickActions.map((action, idx) => (
              <button 
                key={idx}
                className="quick-btn"
                onClick={() => handleQuickAction(action.query)}
                disabled={isLoading || modelDataCache.length === 0}
                data-testid={`quick-action-desktop-${action.label.toLowerCase().replace(' ', '-')}`}
              >
                {action.label}
              </button>
            ))}
          </div>

          <div className="input-area">
            <div className="input-wrapper">
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about quantities, levels, or categories..."
                className="chat-input"
                rows={2}
                disabled={isLoading}
                data-testid="chat-input-desktop"
              />
              <button 
                className="send-btn"
                onClick={askAI}
                disabled={isLoading || !inputText.trim() || modelDataCache.length === 0}
                data-testid="send-btn-desktop"
              >
                <Send size={16} />
              </button>
            </div>
            <div className="input-hint">
              Example: "Total pipe length" or "Show rebar on Level 1"
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
